import { GetServerSideProps } from 'next'

import { CategoryPage } from '@features/category/pages/CategoryPage'
import { AppLayout } from '@components/layouts/AppLayout'
import { CategoryProps } from '@features/category/pages/CategoryPage/interface'
import { gqlApiInstance } from '@client/init'
import { PrimaryTypeEnum } from '@interfaces/PrimaryTypeEnum'
import { OrderedKeyEnum } from '@interfaces/OrderedEnum'
import { SortEnum } from '@interfaces/SortEnum'
import { plainToInstance } from 'class-transformer'
import { CATEGORY } from '@client/collections/Category/schemas/category'
import { CategoryType } from '@models/category/CategoryType'
import { BookEnum } from '@interfaces/BookEnum'
import { BASE_URL } from '@configs/config'
import { OgShare } from '@components/OgShare'
import { isMaintenanceHandler } from '@lib/maintenanceHandler'
import { AppLinkMeta } from '@components/AppLinkMeta'

// test comment

export default function Category({
  categoryId,
  name,
  seoManga,
  seoNovel,
  seoTitleNovel,
  seoDescriptionNovel,
  seoTitleManga,
  seoDescriptionManga,
  bookType,
  isEbook,
  tab,
  orderBy,
  secondaryType,
  advanceSearch,
  url,
}: CategoryProps) {
  // Build query parameters for deeplink
  const queryParams = new URLSearchParams()
  if (categoryId) queryParams.append('id', categoryId.toString())
  if (bookType) queryParams.append('bookType', bookType)
  if (orderBy) queryParams.append('orderBy', orderBy)
  if (secondaryType) queryParams.append('secondaryType', secondaryType)

  const deeplinkUrl = queryParams.toString() ? `?${queryParams.toString()}` : ''

  return (
    <AppLayout
      seoDescription={
        bookType === BookEnum.NOVEL ? seoDescriptionNovel : seoDescriptionManga
      }
    >
      <AppLinkMeta path={`category${deeplinkUrl}`} webUrl={url} />
      <OgShare url={url} />
      <CategoryPage
        category={{
          id: categoryId,
          name,
          seoManga,
          seoNovel,
          seoTitleNovel,
          seoDescriptionNovel,
          seoTitleManga,
          seoDescriptionManga,
        }}
        categoryId={categoryId}
        isEbook={isEbook}
        bookType={bookType}
        tab={tab}
        orderBy={orderBy}
        secondaryType={secondaryType}
        advanceSearch={advanceSearch}
      />
    </AppLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  try {
    if (!query.id) {
      return {
        redirect: {
          destination: '/',
          permanent: false,
        },
      }
    }

    const isMaintenance = await isMaintenanceHandler()
    if (isMaintenance) {
      return {
        redirect: {
          destination: '/maintenance',
          permanent: false,
        },
        props: {
          isMaintenance: !!isMaintenance,
        },
      }
    }

    const res = await gqlApiInstance.request(CATEGORY, {
      categoryId: Number(query.id),
    })
    const {
      name,
      seoManga,
      seoNovel,
      seoTitleNovel,
      seoDescriptionNovel,
      seoTitleManga,
      seoDescriptionManga,
    } = plainToInstance(CategoryType, res.category)
    const tab: PrimaryTypeEnum = [
      PrimaryTypeEnum.HOT_NEW,
      PrimaryTypeEnum.NEW,
      PrimaryTypeEnum.TOP_CHART,
      PrimaryTypeEnum.ENDED,
    ].includes(query.tab as PrimaryTypeEnum)
      ? (query.tab as PrimaryTypeEnum)
      : PrimaryTypeEnum.TOP_CHART
    const seoDescription =
      '"รวมนิยายหลากหลายแนวที่คุณไม่ควรพลาด อ่านง่าย อ่านสนุกที่ MaReads ไม่ว่าจะเป็นแนว นิยายวาย (Yaoi) นิยายยูริ (Yuri) นิยายรัก นิยายรักโรแมนติก นิยายโรแมนซ์ นิยายแฟนตาซี ไซไฟ ผจญภัย นิยายแอคชั่น นิยายกำลังภายใน นิยายจีนโบราณ นิยายสืบสวนสอบสวน นิยายสยองขวัญ นิยายคอมเมดี้ หรือวรรณกรรมเยาวชน หลายเรื่องเป็นเรื่องดังที่เคยถูกทำเป็นซีรีส์มาแล้ว เรายังมีนิยาย อีกมากมาย ทั้งนิยาย Exclusive นิยายแปล นิยายแปลจีน นิยายออริจินัล มาทั้งในรูปแบบของนิยายฟรี นิยาย ฟรี อ่านจบ ไม่ติดเหรียญ และนิยายติดเหรียญ ให้คุณได้เต็มอิ่มไปกับนิยายออนไลน์ และ E-Book ที่อ่านได้ทั้งบนเว็บไซต์ และ แอปพลิเคชัน MaReads แล้วยังให้คุณได้สนับสนุนตัวละครที่คุณรัก เพื่อเป็นกำลังใจให้กับนักเขียน นักวาด และสนับสนุนงานของพวกเขาได้อีกด้วย ซึ่งความพิเศษแบบนี้มีแค่ที่ MaReads เท่านั้น"'
    return {
      props: {
        name,
        seoManga,
        seoNovel,
        seoTitleNovel:
          seoTitleNovel ||
          'อ่านนิยายรายตอน นิยายออนไลน์ อ่านจุใจ ฟินกว่าใครที่ MaReads',
        seoDescriptionNovel: seoDescriptionNovel || seoDescription,
        seoTitleManga:
          seoTitleManga ||
          'อ่านการ์ตูนรายตอน นิยายออนไลน์ อ่านจุใจ ฟินกว่าใครที่ MaReads',
        seoDescriptionManga: seoDescriptionManga || seoDescription,
        categoryId: Number(query.id),
        isEbook: !!query.isEbook,
        bookType: query.bookType || null,
        tab,
        orderBy: query.orderBy || `${OrderedKeyEnum.POPULAR}:${SortEnum.DESC}`,
        secondaryType: query.secondaryType || null,
        advanceSearch: query.advanceSearch === 'true',
        url: `${BASE_URL}${req.url}`,
        isMaintenance: !!isMaintenance,
      },
    }
  } catch (error: any) {
    if (error.response?.status === 503) {
      return {
        redirect: {
          destination: '/maintenance',
          permanent: false,
        },
        props: {
          isMaintenance: true,
        },
      }
    }

    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
      props: {
        isMaintenance: false,
      },
    }
  }
}
